'use client';

import { AdsenseConfiguration } from '@/lib/analytics/adsense';
import * as gtag from '@/lib/analytics/gtag';
import { ENV } from '@/lib/env';
import { cn } from '@ui/lib/utils';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useRef } from 'react';

export interface AdSlot {
  format: AdFormat;
  shape: AdShape;
  size: AdSize;
  layout: AdLayout;
  slot: number;
}

export type AdFormat = 'auto' | 'autorelaxed' | 'fluid' | string;
export type AdSize = 'small' | 'medium' | 'large' | '' | null | undefined;
export type AdShape = 'square' | 'rectangle' | 'leaderboard' | 'half-page' | string;
export type AdLayout = 'in-article' | '' | string;

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

const adSizeClasses: Record<string, Record<string, string> | string> = {
  square: {
    small: 'w-[200px] h-[200px]',
    medium: 'w-[250px] h-[250px]',
  },
  rectangle: {
    medium: 'w-[300px] h-[250px]',
    large: 'w-[336px] h-[280px]',
  },
  leaderboard: {
    small: 'w-[320px] h-[100px] lg:w-[468px] lg:h-[60px]',
    medium: 'w-[320px] h-[100px] lg:w-[728px] lg:h-[90px]',
    large: 'w-[320px] h-[100px] lg:w-[970px] lg:h-[90px]',
  },
  'half-page': 'w-[300px] h-[600px]',
};

function getAdClasses(slotConfig: AdSlot): string {
  const { shape, size } = slotConfig;

  if (shape === 'half-page') {
    return adSizeClasses['half-page'] as string;
  }

  if (shape && size && adSizeClasses[shape]) {
    const sizeMap = adSizeClasses[shape];
    if (typeof sizeMap === 'object' && sizeMap[size]) {
      return sizeMap[size];
    }
  }

  return '';
}

export interface AdBannerProps {
  slot: string;
}

export function AdBanner({ slot }: AdBannerProps) {
  const adRef = useRef<HTMLModElement | null>(null);
  const adsenseConfig = AdsenseConfiguration();
  const slotConfig = adsenseConfig.slots[slot];
  const pathname = usePathname();

  useEffect(() => {
    if (!adRef.current) return;

    // Reset the <ins> element so AdSense can re-initialize it on SPA navigation.
    // Layouts persist across route changes in Next.js App Router, so ads inside
    // layouts would never refresh without this explicit reset.
    const insEl = adRef.current;
    insEl.removeAttribute('data-adsbygoogle-status');
    insEl.removeAttribute('data-ad-status');
    insEl.innerHTML = '';

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (err) {
      console.error('AdBanner error:', err);
    }
  }, [pathname]);

  if (!slotConfig) {
    return (
      <div className="flex items-center justify-center bg-gray-100 my-4 mx-auto p-4 rounded-lg">
        <span className="text-sm text-gray-500">Ad slot &quot;{slot}&quot; not found</span>
      </div>
    );
  }

  const shouldRenderAd = ENV.IS_PRODUCTION_MODE && ENV.GA_ADSENSE_ID;

  return (
    shouldRenderAd ? <div className="flex items-center justify-center bg-gray-100 my-4 mx-auto overflow-hidden min-h-[100px]">
      <Script
        async
        strategy="afterInteractive"
        src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ENV.GA_ADSENSE_ID}`}
        crossOrigin="anonymous"
      />
      <ins
        ref={adRef}
        className={cn('adsbygoogle', getAdClasses(slotConfig))}
        style={{ display: 'block' }}
        data-ad-client={ENV.GA_ADSENSE_ID}
        data-ad-slot={String(slotConfig.slot)}
        data-ad-layout={slotConfig.layout}
        data-ad-format={slotConfig.format}
        data-full-width-responsive="true"
      />
    </div> : <div className="flex items-center justify-center bg-gray-100 border-red-500 border my-4 mx-auto overflow-hidden min-h-[100px]"></div>
  );
}
